import { Static, Type } from '@sinclair/typebox';
import { EmailConfigSchema } from './email_types/EmailTypes';
import { NotificationType, NotificationTheme } from './translation_namespace/notification_namespace';

export enum NotificationChannel {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  PUSH = 'PUSH',
  IN_APP = 'IN_APP',
}

export enum NotificationLogStatus {
  QUEUED = 'QUEUED',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export enum NotificationRecipientType {
  CUSTOMER = 'CUSTOMER',
  PROFESSIONAL = 'PROFESSIONAL',
  TEAM_MEMBER = 'TEAM_MEMBER',
}

export enum NotificationDevicePlatform {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  WEB = 'WEB',
}

export const NotificationChannelSchema = Type.Union(
  Object.values(NotificationChannel).map((v: string): ReturnType<typeof Type.Literal> => Type.Literal(v)),
);

export const NotificationLogStatusSchema = Type.Union(
  Object.values(NotificationLogStatus).map((v: string): ReturnType<typeof Type.Literal> => Type.Literal(v)),
);

/**
 * Point d'ancrage du délai d'un événement séquencé : le délai se compte AVANT le
 * début du RDV (rappels) ou APRÈS sa fin (demande d'avis). Source unique lue par
 * le backend (délais par défaut) ET le front (champ `delay_minutes` vs
 * `delay_after_minutes`, libellé « avant/après le rdv ») — ne jamais retester
 * l'event en dur à ces endroits.
 */
export enum NotificationDelayAnchor {
  BEFORE_APPOINTMENT = 'BEFORE_APPOINTMENT',
  AFTER_APPOINTMENT = 'AFTER_APPOINTMENT',
}

export interface NotificationEventCatalogEntry {
  event_type: NotificationType;
  audience: 'CUSTOMER' | 'PRO';
  allowed_channels: NotificationChannel[];
  default_channels: NotificationChannel[];
  is_marketing: boolean;
  supports_sequences: boolean;
  /** Transactional system event (Propelsia is the sender, not the pro). Skips quota, skips notification_preference, hidden from /marketing/notifications/. */
  is_system: boolean;
  /** Thème fonctionnel — regroupe les messages automatiques par sujet dans /marketing/notifications. */
  theme: NotificationTheme;
  /** Renseigné uniquement pour les events dont le délai est relatif au RDV (cf. NotificationDelayAnchor). */
  delay_anchor?: NotificationDelayAnchor;
  /** Événement du pack référencement : masqué de /marketing/notifications tant que le pack n'est pas ouvert au compte. */
  requires_seo_pack?: boolean;
  /**
   * Événement des promotions : masqué de /marketing/notifications tant que la page Promotions est fermée à la
   * session (pré-lancement, `isPromotionsOpen`) ; le backend ne l'envoie que là où elle est ouverte à tous
   * (`isPreLaunchOpenEnvironment`).
   */
  requires_promotions?: boolean;
}

export const NOTIFICATION_EVENT_CATALOG: Record<NotificationType, NotificationEventCatalogEntry> = {
  [NotificationType.BOOKING_CONFIRMED]: {
    event_type: NotificationType.BOOKING_CONFIRMED,
    theme: NotificationTheme.BOOKING,
    audience: 'CUSTOMER',
    // PUSH retiré des events client : les clients finaux n'ont pas d'app, le push
    // ne part jamais et polluait l'UI pro. À ré-ajouter le jour où le push client existe.
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.BOOKING_WAITING_CONFIRMATION]: {
    event_type: NotificationType.BOOKING_WAITING_CONFIRMATION,
    theme: NotificationTheme.BOOKING,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.BOOKING_REMINDER]: {
    event_type: NotificationType.BOOKING_REMINDER,
    theme: NotificationTheme.BOOKING,
    audience: 'CUSTOMER',
    // PUSH retiré des events client (cf. BOOKING_CONFIRMED).
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    // SMS par défaut pour tous les pros : rappel email 48h avant + SMS 24h avant
    // (cf. NOTIFICATION_DEFAULT_REMINDER_DELAY_MINUTES). Iso comportement v1
    // (email la veille + SMS 24h), en relatif — l'heure d'envoi hérite de
    // l'heure du RDV, donc jamais d'envoi nocturne.
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    is_marketing: false,
    supports_sequences: true,
    is_system: false,
    delay_anchor: NotificationDelayAnchor.BEFORE_APPOINTMENT,
  },
  [NotificationType.BOOKING_CANCELED_BY_PRO]: {
    event_type: NotificationType.BOOKING_CANCELED_BY_PRO,
    theme: NotificationTheme.BOOKING,
    audience: 'CUSTOMER',
    // PUSH retiré des events client (cf. BOOKING_CONFIRMED).
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.BOOKING_CANCELED_BY_CUSTOMER]: {
    event_type: NotificationType.BOOKING_CANCELED_BY_CUSTOMER,
    theme: NotificationTheme.BOOKING,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.BOOKING_UPDATED]: {
    event_type: NotificationType.BOOKING_UPDATED,
    theme: NotificationTheme.BOOKING,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.REVIEW_REQUEST]: {
    event_type: NotificationType.REVIEW_REQUEST,
    theme: NotificationTheme.REVIEW,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: true,
    supports_sequences: true,
    is_system: false,
    delay_anchor: NotificationDelayAnchor.AFTER_APPOINTMENT,
  },
  [NotificationType.VOUCHER_PURCHASED]: {
    event_type: NotificationType.VOUCHER_PURCHASED,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.VOUCHER_GIFT_RECEIVED]: {
    event_type: NotificationType.VOUCHER_GIFT_RECEIVED,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.VOUCHER_EXPIRING]: {
    event_type: NotificationType.VOUCHER_EXPIRING,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.VOUCHER_USED]: {
    event_type: NotificationType.VOUCHER_USED,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.VOUCHER_RECHARGED]: {
    event_type: NotificationType.VOUCHER_RECHARGED,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.VOUCHER_CANCELLED]: {
    event_type: NotificationType.VOUCHER_CANCELLED,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.VOUCHER_RESTORED]: {
    event_type: NotificationType.VOUCHER_RESTORED,
    theme: NotificationTheme.VOUCHER,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_NEW_BOOKING]: {
    event_type: NotificationType.PRO_NEW_BOOKING,
    theme: NotificationTheme.BOOKING,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.PUSH, NotificationChannel.IN_APP],
    // PUSH par défaut : l'app mobile du pro doit sonner sur une nouvelle résa sans
    // qu'il ait à trouver le toggle ; sans device enregistré le processor skippe
    // proprement (NO_DEVICE_REGISTERED), et l'opt-out reste possible par préférence.
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_BOOKING_CANCELED]: {
    event_type: NotificationType.PRO_BOOKING_CANCELED,
    theme: NotificationTheme.BOOKING,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.PUSH, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_BOOKING_REMINDER]: {
    event_type: NotificationType.PRO_BOOKING_REMINDER,
    theme: NotificationTheme.BOOKING,
    audience: 'PRO',
    // Pendant du BOOKING_REMINDER client, côté pro : « préviens-MOI avant chaque RDV ».
    // Remplace l'ancien booléen pro_booking_params.notify_pro_before_booking (jamais branché),
    // pour que le pro règle canaux ET délais depuis /marketing/notifications comme le reste.
    allowed_channels: [
      NotificationChannel.EMAIL,
      NotificationChannel.SMS,
      NotificationChannel.PUSH,
      NotificationChannel.IN_APP,
    ],
    // PUSH uniquement par défaut. SMS et EMAIL restent disponibles mais en opt-in
    // explicite : ils sont décomptés du quota du pro à CHAQUE rendez-vous
    // (cf. channelDecrementsQuota), et un pro qui n'a rien demandé recevait un SMS à H-2
    // pour chacun de ses propres RDV — coûteux et non sollicité. Le « le pro coupe ce
    // qu'il ne veut pas » ne tient pas non plus : régler les canaux est réservé à
    // l'option Marque blanche, donc un pro sans l'option ne peut PAS se désabonner.
    // IN_APP retiré aussi : une cloche pour chaque RDV à venir n'apporte rien.
    default_channels: [NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: true,
    is_system: false,
    delay_anchor: NotificationDelayAnchor.BEFORE_APPOINTMENT,
  },
  [NotificationType.PRO_NEW_REVIEW]: {
    event_type: NotificationType.PRO_NEW_REVIEW,
    theme: NotificationTheme.REVIEW,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.PUSH, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_VOUCHER_SOLD]: {
    event_type: NotificationType.PRO_VOUCHER_SOLD,
    theme: NotificationTheme.VOUCHER,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.PUSH, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_QUOTA_LOW]: {
    event_type: NotificationType.PRO_QUOTA_LOW,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PAYMENT_REFUNDED]: {
    event_type: NotificationType.PAYMENT_REFUNDED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  // Lien de paiement du pro : jamais automatique, envoyé seulement quand le pro le demande (case à la
  // création, boutons « Envoyer par email / SMS »). Transactionnel : ignore le refus marketing du client.
  [NotificationType.PAYMENT_REQUEST]: {
    event_type: NotificationType.PAYMENT_REQUEST,
    theme: NotificationTheme.ACTIVITY,
    audience: 'CUSTOMER',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.SMS],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_BOOKING_RESCHEDULED]: {
    event_type: NotificationType.PRO_BOOKING_RESCHEDULED,
    theme: NotificationTheme.BOOKING,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.PUSH, NotificationChannel.IN_APP],
    // Même triplé par défaut que PRO_NEW_BOOKING : une résa déplacée par le client
    // change l'agenda du pro, il doit le voir partout.
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_PAYMENT_RECEIVED]: {
    event_type: NotificationType.PRO_PAYMENT_RECEIVED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.PUSH, NotificationChannel.IN_APP],
    // PUSH par défaut : un encaissement mérite le téléphone (skip propre
    // NO_DEVICE_REGISTERED sans device, opt-out par préférence).
    default_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_NEW_CONTACT_MESSAGE]: {
    event_type: NotificationType.PRO_NEW_CONTACT_MESSAGE,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.PUSH, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.PRO_SUPPORT_AGENT_REPLY]: {
    event_type: NotificationType.PRO_SUPPORT_AGENT_REPLY,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    // In-app (cloche web) + push (app mobile) : le webhook Chatwoot ne dispatche
    // que si le pro n'est PAS présent sur /support (heartbeat frais, cf.
    // SupportNotifyProcessor) — la notif sert uniquement à le prévenir quand il
    // est absent. Le push retombe sur le template IN_APP et deep-linke vers le fil
    // (`/support?conversation_id=…`, related_entity_type `support_conversation`).
    // Email à ajouter plus tard.
    allowed_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  [NotificationType.TEAM_MEMBER_INVITATION]: {
    event_type: NotificationType.TEAM_MEMBER_INVITATION,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.ACCOUNT_PASSWORD_RESET]: {
    event_type: NotificationType.ACCOUNT_PASSWORD_RESET,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Relances impayé abonnement (dunning) — cf. billing/dunning-notification.service côté backend.
  // Tous `is_system: true` : Propelsia est l'émetteur, et le flag court-circuite quota ET gel
  // d'accès (sans lui, impossible de prévenir un pro déjà gelé de son propre gel). Les canaux
  // effectifs varient par étape du calendrier (payload.channels ⊆ allowed_channels) ;
  // `default_channels` n'est que le repli si l'émetteur ne précise rien.
  [NotificationType.PRO_BILLING_PAYMENT_FAILED]: {
    event_type: NotificationType.PRO_BILLING_PAYMENT_FAILED,
    theme: NotificationTheme.BILLING,
    audience: 'PRO',
    allowed_channels: [
      NotificationChannel.EMAIL,
      NotificationChannel.SMS,
      NotificationChannel.PUSH,
      NotificationChannel.IN_APP,
    ],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_BILLING_PAYMENT_METHOD_MISSING]: {
    event_type: NotificationType.PRO_BILLING_PAYMENT_METHOD_MISSING,
    theme: NotificationTheme.BILLING,
    audience: 'PRO',
    allowed_channels: [
      NotificationChannel.EMAIL,
      NotificationChannel.SMS,
      NotificationChannel.PUSH,
      NotificationChannel.IN_APP,
    ],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_BILLING_SUSPENSION_NOTICE]: {
    event_type: NotificationType.PRO_BILLING_SUSPENSION_NOTICE,
    theme: NotificationTheme.BILLING,
    audience: 'PRO',
    allowed_channels: [
      NotificationChannel.EMAIL,
      NotificationChannel.SMS,
      NotificationChannel.PUSH,
      NotificationChannel.IN_APP,
    ],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_BILLING_SEMI_FROZEN]: {
    event_type: NotificationType.PRO_BILLING_SEMI_FROZEN,
    theme: NotificationTheme.BILLING,
    audience: 'PRO',
    allowed_channels: [
      NotificationChannel.EMAIL,
      NotificationChannel.SMS,
      NotificationChannel.PUSH,
      NotificationChannel.IN_APP,
    ],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_BILLING_PAST_DUE]: {
    event_type: NotificationType.PRO_BILLING_PAST_DUE,
    theme: NotificationTheme.BILLING,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.PUSH, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_BILLING_PAYMENT_RECOVERED]: {
    event_type: NotificationType.PRO_BILLING_PAYMENT_RECOVERED,
    theme: NotificationTheme.BILLING,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Code OTP de connexion à l'espace affilié (affiliation v2, Lot 5). Event système :
  // pas de professional associé (l'affilié n'est pas forcément client), pas de préférences.
  [NotificationType.AFFILIATE_LOGIN_OTP]: {
    event_type: NotificationType.AFFILIATE_LOGIN_OTP,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Validation admin d'un compte affilié auto-inscrit : c'est le SEUL signal qui dit à
  // l'affilié que son lien est actif. Event système (pas de professional, pas de préférences).
  [NotificationType.AFFILIATE_ACCOUNT_APPROVED]: {
    event_type: NotificationType.AFFILIATE_ACCOUNT_APPROVED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Cycle de vie du parrainage (affiliation v2) — l'ambassadeur est tenu au courant à
  // chaque étape : filleul inscrit, filleul devenu client, commission encaissée.
  //
  // ⚠️ ANTI-DOUBLON FIRSTPROMOTER : ces trois events ne sont émis QUE par notre moteur
  // (attribution au signup v2, `recordForSegment`). Le workflow n8n v1 continue de tourner
  // sur les webhooks FP et couvre déjà les mêmes étapes côté v1 — d'où la règle absolue :
  // `FirstpromoterSyncService` n'émet JAMAIS de notification (il n'a même pas accès au
  // service qui les envoie).
  [NotificationType.AFFILIATE_REFERRAL_SIGNUP]: {
    event_type: NotificationType.AFFILIATE_REFERRAL_SIGNUP,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.AFFILIATE_REFERRAL_SUBSCRIBED]: {
    event_type: NotificationType.AFFILIATE_REFERRAL_SUBSCRIBED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.AFFILIATE_COMMISSION_EARNED]: {
    event_type: NotificationType.AFFILIATE_COMMISSION_EARNED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_CUSTOMER_IMPORT_COMPLETED]: {
    event_type: NotificationType.PRO_CUSTOMER_IMPORT_COMPLETED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
  },
  // Bascule v1 → v2. Le destinataire est un pro CFIXÉ : il n'a pas forcément de compte v2, donc
  // l'envoi se fait en système avec `professional_id: null` et une adresse email brute (même
  // canal que les emails d'affiliation). Email seul : ni SMS ni push ne sont pertinents ici.
  [NotificationType.PRO_MIGRATION_DAY]: {
    event_type: NotificationType.PRO_MIGRATION_DAY,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.PRO_MIGRATION_DONE]: {
    event_type: NotificationType.PRO_MIGRATION_DONE,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Changement d'adresse email (cf. backend email_change/) : events système, email seul. Le
  // destinataire n'est pas forcément joignable ailleurs (nouvelle adresse pas encore vérifiée).
  [NotificationType.EMAIL_CHANGE_CONFIRM]: {
    event_type: NotificationType.EMAIL_CHANGE_CONFIRM,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.EMAIL_CHANGE_ALERT]: {
    event_type: NotificationType.EMAIL_CHANGE_ALERT,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  [NotificationType.EMAIL_CHANGE_DONE]: {
    event_type: NotificationType.EMAIL_CHANGE_DONE,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Email de bienvenue à l'inscription (relance des essais, lot 0). Event système :
  // Propelsia est l'émetteur, hors quota et hors préférences ; email seul, le SMS J0+4h
  // et les relances J1 → J7 relèvent de PRO_TRIAL_RELANCE (lot 1).
  [NotificationType.PRO_TRIAL_WELCOME]: {
    event_type: NotificationType.PRO_TRIAL_WELCOME,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Relances J0+4h → J7 de l'essai (moteur trial_relance, lot 1) : les canaux sont restreints
  // par moment via `payload.channels` (email J1/J3/J5/J7, push J2/J6, SMS J0+4h/J2/J6).
  [NotificationType.PRO_TRIAL_RELANCE]: {
    event_type: NotificationType.PRO_TRIAL_RELANCE,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [
      NotificationChannel.EMAIL,
      NotificationChannel.SMS,
      NotificationChannel.PUSH,
      NotificationChannel.IN_APP,
    ],
    default_channels: [NotificationChannel.EMAIL],
    is_marketing: false,
    supports_sequences: false,
    is_system: true,
  },
  // Articles SEO (plan MAX) : le pro valide ses sujets puis chaque article. Email avec bouton
  // « valider en un clic », cloche in-app et push vers l'écran /marketing/seo.
  [NotificationType.PRO_SEO_TOPICS_TO_VALIDATE]: {
    event_type: NotificationType.PRO_SEO_TOPICS_TO_VALIDATE,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_seo_pack: true,
  },
  [NotificationType.PRO_SEO_ARTICLES_READY]: {
    event_type: NotificationType.PRO_SEO_ARTICLES_READY,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_seo_pack: true,
  },
  // Backlink du pack en ligne : cloche + push seulement, jamais d'e-mail (pas même sur préférence) :
  // l'information vit dans l'onglet Backlinks.
  [NotificationType.PRO_SEO_BACKLINK_PUBLISHED]: {
    event_type: NotificationType.PRO_SEO_BACKLINK_PUBLISHED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_seo_pack: true,
  },
  // Mode B : relecture attendue sous 7 jours, e-mail compris (sans validation, le mois est perdu).
  [NotificationType.PRO_SEO_BACKLINK_TO_REVIEW]: {
    event_type: NotificationType.PRO_SEO_BACKLINK_TO_REVIEW,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_seo_pack: true,
  },
  [NotificationType.PRO_SEO_BACKLINK_EXPIRED]: {
    event_type: NotificationType.PRO_SEO_BACKLINK_EXPIRED,
    theme: NotificationTheme.ACTIVITY,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.EMAIL, NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_seo_pack: true,
  },
  // Promotions (spec Promotions § 9) : cloche par défaut, push sur demande (même texte), jamais d'e-mail.
  // Chacune part une seule fois par promotion (horodatages de `pro_promo_code`, cron horaire du worker).
  [NotificationType.PRO_PROMOTION_STARTS_TOMORROW]: {
    event_type: NotificationType.PRO_PROMOTION_STARTS_TOMORROW,
    theme: NotificationTheme.PROMOTION,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_promotions: true,
  },
  [NotificationType.PRO_PROMOTION_QUOTA_ALMOST_REACHED]: {
    event_type: NotificationType.PRO_PROMOTION_QUOTA_ALMOST_REACHED,
    theme: NotificationTheme.PROMOTION,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_promotions: true,
  },
  [NotificationType.PRO_PROMOTION_ENDED]: {
    event_type: NotificationType.PRO_PROMOTION_ENDED,
    theme: NotificationTheme.PROMOTION,
    audience: 'PRO',
    allowed_channels: [NotificationChannel.IN_APP, NotificationChannel.PUSH],
    default_channels: [NotificationChannel.IN_APP],
    is_marketing: false,
    supports_sequences: false,
    is_system: false,
    requires_promotions: true,
  },
};

export const NOTIFICATION_DEFAULT_REMINDER_DELAY_MINUTES: Record<NotificationChannel, number> = {
  [NotificationChannel.EMAIL]: 2880,
  [NotificationChannel.SMS]: 1440,
  [NotificationChannel.PUSH]: 120,
  [NotificationChannel.IN_APP]: 0,
};

/**
 * Délais du rappel adressé AU PRO — distincts de ceux du client : le pro n'a pas
 * besoin d'être prévenu 48 h avant, il veut l'alerte juste avant le RDV (push/cloche)
 * et un récap la veille (email/SMS).
 */
export const NOTIFICATION_DEFAULT_PRO_REMINDER_DELAY_MINUTES: Record<NotificationChannel, number> = {
  [NotificationChannel.EMAIL]: 1440,
  [NotificationChannel.SMS]: 120,
  [NotificationChannel.PUSH]: 30,
  [NotificationChannel.IN_APP]: 30,
};

/**
 * Délais par défaut par canal, par événement. Source unique consommée par le
 * scheduler backend, les entrées de préférence synthétiques et le front (ajout
 * d'un canal / d'une séquence) — sinon l'UI affiche un délai vide alors que
 * l'envoi part bien à H-x.
 */
const NOTIFICATION_DEFAULT_DELAY_MINUTES_BY_EVENT: Partial<
  Record<NotificationType, Record<NotificationChannel, number>>
> = {
  [NotificationType.BOOKING_REMINDER]: NOTIFICATION_DEFAULT_REMINDER_DELAY_MINUTES,
  [NotificationType.PRO_BOOKING_REMINDER]: NOTIFICATION_DEFAULT_PRO_REMINDER_DELAY_MINUTES,
};

/** Délai par défaut (en minutes) d'un canal pour un événement, `null` si l'événement n'est pas différé. */
export function getDefaultDelayMinutes(event: NotificationType, channel: NotificationChannel): number | null {
  return NOTIFICATION_DEFAULT_DELAY_MINUTES_BY_EVENT[event]?.[channel] ?? null;
}

/**
 * Séquence de demande d'avis par défaut. L'étape SMS est proposée mais désactivée
 * (`enabled: false`) : le pro l'active d'un interrupteur, sans l'option Marque blanche
 * (ajouter une étape, elle, en dépend). Un pro qui avait déjà enregistré sa séquence
 * la voit aussi, désactivée (cf. `NotificationPreferenceService.list`).
 */
export const NOTIFICATION_DEFAULT_REVIEW_SEQUENCES: Array<{
  channel: NotificationChannel;
  delay_after_minutes: number;
  sequence_order: number;
  enabled: boolean;
}> = [
  { channel: NotificationChannel.EMAIL, delay_after_minutes: 120, sequence_order: 0, enabled: true },
  { channel: NotificationChannel.SMS, delay_after_minutes: 120, sequence_order: 1, enabled: false },
  { channel: NotificationChannel.EMAIL, delay_after_minutes: 10080, sequence_order: 2, enabled: true },
];

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/**
 * Formes d'affichage compact d'un délai. Une clé = une phrase complète (jamais
 * de concaténation côté consommateur) pour que chaque locale garde la main sur
 * l'ordre des unités et les séparateurs.
 */
export type NotificationDelayTranslationKey =
  | 'NOTIFICATIONS_DELAY_IMMEDIATE'
  | 'NOTIFICATIONS_DELAY_MINUTES_SHORT'
  | 'NOTIFICATIONS_DELAY_HOURS_SHORT'
  | 'NOTIFICATIONS_DELAY_HOURS_MINUTES_SHORT'
  | 'NOTIFICATIONS_DELAY_DAYS_SHORT'
  | 'NOTIFICATIONS_DELAY_DAYS_HOURS_SHORT';

export interface NotificationDelayFormatted {
  translation_key: NotificationDelayTranslationKey;
  /** Valeurs d'interpolation attendues par la clé (à passer telles quelles au `t()`). */
  values: { days?: number; hours?: number; minutes?: number };
}

/**
 * Formate un délai (minutes) dans l'unité la plus lisible, au plus deux unités
 * (j + h, ou h + min). Ne JAMAIS tout ramener à l'heure : un délai de 2 minutes
 * s'affichait « 0h », c'est-à-dire un délai non nul rendu comme s'il n'existait pas.
 */
export function formatNotificationDelayMinutes(minutes: number): NotificationDelayFormatted {
  const total = Math.max(0, Math.round(minutes));
  if (total === 0) return { translation_key: 'NOTIFICATIONS_DELAY_IMMEDIATE', values: {} };

  if (total < MINUTES_PER_HOUR) {
    return { translation_key: 'NOTIFICATIONS_DELAY_MINUTES_SHORT', values: { minutes: total } };
  }

  if (total < MINUTES_PER_DAY) {
    const hours = Math.floor(total / MINUTES_PER_HOUR);
    const rest = total % MINUTES_PER_HOUR;
    return rest === 0
      ? { translation_key: 'NOTIFICATIONS_DELAY_HOURS_SHORT', values: { hours } }
      : { translation_key: 'NOTIFICATIONS_DELAY_HOURS_MINUTES_SHORT', values: { hours, minutes: rest } };
  }

  const days = Math.floor(total / MINUTES_PER_DAY);
  const hours = Math.round((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  // 1 j 23 h 59 arrondit à 24 h : on reporte sur le jour plutôt qu'afficher « 1 j 24 h ».
  if (hours === 24) return { translation_key: 'NOTIFICATIONS_DELAY_DAYS_SHORT', values: { days: days + 1 } };
  return hours === 0
    ? { translation_key: 'NOTIFICATIONS_DELAY_DAYS_SHORT', values: { days } }
    : { translation_key: 'NOTIFICATIONS_DELAY_DAYS_HOURS_SHORT', values: { days, hours } };
}

/**
 * Unité d'affichage/saisie d'un délai de notification. La minute reste l'unité
 * canonique stockée en base ; ces helpers ne servent qu'à la conversion UI.
 */
export type NotificationDelayUnit = 'minutes' | 'hours' | 'days';

export const NOTIFICATION_DELAY_UNITS: readonly NotificationDelayUnit[] = ['minutes', 'hours', 'days'];

const NOTIFICATION_DELAY_UNIT_FACTORS: Record<NotificationDelayUnit, number> = {
  minutes: 1,
  hours: MINUTES_PER_HOUR,
  days: MINUTES_PER_DAY,
};

/** Convertit une valeur saisie dans une unité donnée en minutes (entier ≥ 0). */
export function composeDelayMinutes(value: number, unit: NotificationDelayUnit): number {
  return Math.max(0, Math.round(value * NOTIFICATION_DELAY_UNIT_FACTORS[unit]));
}

/** Décompose des minutes en {valeur, unité} en privilégiant la plus grande unité « ronde ». */
export function decomposeDelayMinutes(minutes: number): { value: number; unit: NotificationDelayUnit } {
  if (minutes <= 0) return { value: 0, unit: 'hours' };
  if (minutes % MINUTES_PER_DAY === 0) return { value: minutes / MINUTES_PER_DAY, unit: 'days' };
  if (minutes % MINUTES_PER_HOUR === 0) return { value: minutes / MINUTES_PER_HOUR, unit: 'hours' };
  return { value: minutes, unit: 'minutes' };
}

const NotificationEventTypeSchema = Type.Union(
  Object.values(NotificationType).map((value: string): ReturnType<typeof Type.Literal> => Type.Literal(value)),
);

export const NotificationActivityResponseSchema = Type.Object({
  notification_activity_id: Type.Number(),
  event_type: NotificationEventTypeSchema,
  channel: Type.Union([NotificationChannelSchema, Type.Null()]),
  title: Type.String(),
  body: Type.Union([Type.String(), Type.Null()]),
  read: Type.Boolean(),
  related_entity_type: Type.Union([Type.String(), Type.Null()]),
  related_entity_id: Type.Union([Type.Number(), Type.Null()]),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
});
export type NotificationActivityResponseObject = Static<typeof NotificationActivityResponseSchema>;

export const ListNotificationActivityResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(NotificationActivityResponseSchema),
});
export type ListNotificationActivityResponseObject = Static<typeof ListNotificationActivityResponseSchema>;

export const NotificationUnreadCountResponseSchema = Type.Object({
  unread: Type.Number(),
});
export type NotificationUnreadCountResponseObject = Static<typeof NotificationUnreadCountResponseSchema>;

export const NotificationPreferenceEntrySchema = Type.Object({
  channel: NotificationChannelSchema,
  enabled: Type.Boolean(),
  sequence_order: Type.Integer({ minimum: 0 }),
  delay_minutes: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
  delay_after_minutes: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
  custom_subject: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  custom_body: Type.Optional(Type.Union([Type.String(), EmailConfigSchema, Type.Null()])),
  custom_push_title: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  custom_push_body: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});
export type NotificationPreferenceEntryObject = Static<typeof NotificationPreferenceEntrySchema>;

export const NotificationDefaultPreviewSchema = Type.Object({
  email: Type.Optional(
    Type.Object({
      subject: Type.String(),
      body_text_preview: Type.String(),
      config: EmailConfigSchema,
    }),
  ),
  sms: Type.Optional(
    Type.Object({
      body: Type.String(),
    }),
  ),
  in_app: Type.Optional(
    Type.Object({
      title: Type.String(),
      body: Type.Union([Type.String(), Type.Null()]),
    }),
  ),
});
export type NotificationDefaultPreviewObject = Static<typeof NotificationDefaultPreviewSchema>;

export const NotificationPreferenceByEventSchema = Type.Object({
  event_type: Type.Union(
    Object.values(NotificationType).map((v: string): ReturnType<typeof Type.Literal> => Type.Literal(v)),
  ),
  entries: Type.Array(NotificationPreferenceEntrySchema),
  defaults: Type.Optional(NotificationDefaultPreviewSchema),
});
export type NotificationPreferenceByEventObject = Static<typeof NotificationPreferenceByEventSchema>;

export const ListNotificationPreferenceResponseSchema = Type.Object({
  items: Type.Array(NotificationPreferenceByEventSchema),
});
export type ListNotificationPreferenceResponseObject = Static<typeof ListNotificationPreferenceResponseSchema>;

export const UpdateNotificationPreferenceRequestSchema = Type.Object({
  entries: Type.Array(NotificationPreferenceEntrySchema),
});
export type UpdateNotificationPreferenceRequestObject = Static<typeof UpdateNotificationPreferenceRequestSchema>;

export const NotificationHistoryItemSchema = Type.Object({
  notification_log_id: Type.Number(),
  event_type: NotificationEventTypeSchema,
  channel: NotificationChannelSchema,
  status: NotificationLogStatusSchema,
  error_message: Type.Union([Type.String(), Type.Null()]),
  provider_name: Type.Union([Type.String(), Type.Null()]),
  sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  ),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
  /** Bouton « Renvoyer » : notification ignorée/en échec toujours d'actualité (backend notification-resend.policy.ts). */
  can_resend: Type.Boolean(),
});
export type NotificationHistoryItemObject = Static<typeof NotificationHistoryItemSchema>;

export const ListNotificationHistoryResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(NotificationHistoryItemSchema),
});
export type ListNotificationHistoryResponseObject = Static<typeof ListNotificationHistoryResponseSchema>;

export const NotificationDevicePlatformSchema = Type.Union([
  Type.Literal(NotificationDevicePlatform.IOS),
  Type.Literal(NotificationDevicePlatform.ANDROID),
]);

export const UpsertNotificationDeviceRequestSchema = Type.Object({
  platform: NotificationDevicePlatformSchema,
  token: Type.String({ minLength: 1, maxLength: 500 }),
});
export type UpsertNotificationDeviceRequestObject = Static<typeof UpsertNotificationDeviceRequestSchema>;

export const DeleteNotificationDeviceRequestSchema = Type.Object({
  token: Type.String({ minLength: 1, maxLength: 500 }),
});
export type DeleteNotificationDeviceRequestObject = Static<typeof DeleteNotificationDeviceRequestSchema>;

export const NotificationDeviceResponseSchema = Type.Object({
  notification_device_id: Type.Number(),
  platform: NotificationDevicePlatformSchema,
  active: Type.Boolean(),
});
export type NotificationDeviceResponseObject = Static<typeof NotificationDeviceResponseSchema>;
